import {
  Component, Input, Output, EventEmitter, forwardRef,
  OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef, ContentChild, TemplateRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { SelectOption } from '../primitives.types';
import { KtSelectPanelDirective } from './select-panel.directive';

@Component({
  selector: 'kt-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true }]
})
export class SelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() label = '';
  /** Where the label sits relative to the control. Default 'top'; use 'left'/'right' for inline layouts (e.g. toolbars with limited height). */
  @Input() labelPosition: 'top' | 'left' | 'right' | 'bottom' = 'top';
  /** When set, clamps the label to this many lines and reserves that height, so labels of
   * differing length stay vertically aligned across a row of fields. Unset = natural height. */
  @Input() labelLines: number = null;
  /** Hover tooltip for the select trigger. */
  @Input() tooltip = '';
  @Input() placeholder = 'Select...';
  @Input() options: SelectOption[] = [];
  @Input() multiple = false;
  @Input() searchable = false;
  @Input() virtualScroll = false;
  @Input() itemSize = 40;
  @Input() disabled = false;
  @Input() required = false;
  @Input() clearable = false;
  @Input() loading = false;
  @Input() hint = '';
  @Input() errorMessage = '';
  /** How errorMessage is shown: 'inline' below the field (default, reserves layout space) or 'floating' as an overlay balloon that doesn't push content below. */
  @Input() errorDisplay: 'inline' | 'floating' = 'inline';
  @Input() noResultsText = 'No results found';
  @Input() searchPlaceholder = 'Search...';
  @Input() panelClass = '';
  @Input() maxChipsVisible = 3;
  @Input() customPanel = false;
  @Input() triggerText = '';
  /** Multi-select only: adds a "Select all" row; with grouped options, also a per-group toggle checkbox. */
  @Input() showSelectAll = false;
  @Input() selectAllLabel = 'Select all';
  /** Grouped + select-all only: also render a single top-level "Select all" row spanning every group. */
  @Input() showSelectAllAcrossGroups = false;
  /** Grouped options: render each group header with an expand/collapse chevron (single-select). */
  @Input() collapsibleGroups = false;
  /** Collapsible groups only: start every group collapsed instead of expanded. */
  @Input() groupsCollapsedByDefault = false;
  /** groupsCollapsedByDefault only: collapse each group on first appearance, not just once overall. */
  @Input() collapseNewGroupsIndividually = false;
  /**
   * Standard (non-virtual) mode only: render OK/Cancel buttons in the panel. The bound value still
   * updates live as the user selects (so the trigger stays in sync), but the "apply" notification —
   * `selectionChange` and `confirm` — is DEFERRED until OK. Cancel restores the selection captured when
   * the panel opened. Use when reacting to a selection (via `confirm`) triggers expensive work.
   */
  @Input() confirmFooter = false;
  @Input() confirmLabel = 'OK';
  @Input() cancelLabel = 'Cancel';
  /**
   * Standard (non-virtual) mode: when the search query matches no existing option label, render a
   * "create" row at the bottom of the panel. Clicking it emits `createOption` with the typed text —
   * the consumer is responsible for creating the entity and (if desired) selecting it.
   */
  @Input() creatable = false;
  @Input() createLabelPrefix = '+ Create';

  @Output() searchChange = new EventEmitter<string>();
  /** Emits the new value on user-driven selection changes (not on programmatic writeValue). In confirmFooter mode, only on OK. */
  @Output() selectionChange = new EventEmitter<any>();
  @Output() scrolledToEnd = new EventEmitter<void>();
  @Output() panelOpenedChange = new EventEmitter<boolean>();
  /** confirmFooter mode: emitted with the applied value when OK is clicked. */
  @Output() confirm = new EventEmitter<any>();
  /** confirmFooter mode: emitted when Cancel is clicked (selection restored to the panel-open snapshot). */
  @Output() cancel = new EventEmitter<void>();
  /** `creatable` mode: emitted with the trimmed search query when the user clicks the "create" row. */
  @Output() createOption = new EventEmitter<string>();

  @ViewChild('matSelectRef') matSelectRef: MatSelect;
  @ViewChild('triggerRef') triggerRef: ElementRef<HTMLDivElement>;
  @ViewChild('vsSearchInput') vsSearchInput: ElementRef<HTMLInputElement>;
  @ContentChild(KtSelectPanelDirective) customPanelDir: KtSelectPanelDirective;
  /** Optional per-option content (standard mode). Falls back to `opt.label` when not provided. */
  @ContentChild('ktOptionContent') optionContent: TemplateRef<any>;

  value: any = null;
  /** confirmFooter mode: value when the panel opened, restored if the user cancels. */
  private confirmSnapshot: any = null;
  searchQuery = '';
  filteredOptions: SelectOption[] = [];
  groupedFilteredOptions: { name: string; options: SelectOption[] }[] = [];
  hasGroups = false;
  isOpen = false;
  triggerWidth = 240;

  onChange = (_: any) => { };
  onTouched = () => { };

  // ── Scroll-jump guard ──────────────────────────────────────
  // mat-select scrolls the "active" option into view after every selection using a uniform
  // item-height model. With grouped options that have variable-height headers, a select-all
  // row, and collapsed (display:none) options, that model is wrong, so the panel visibly jumps
  // up/down on each toggle. We snapshot the panel scrollTop on mousedown (before the click
  // mutates it) and restore it right after the value changes.
  private savedScrollTop: number | null = null;
  private guardedPanel: HTMLElement | null = null;
  private readonly scrollGuardHandler = () => {
    if (this.guardedPanel) this.savedScrollTop = this.guardedPanel.scrollTop;
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) this.applyFilter();
  }

  private applyFilter() {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredOptions = q
      ? this.options.filter(o => o.label.toLowerCase().includes(q) || (o.group || '').toLowerCase().includes(q))
      : [...this.options];
    this.buildGroups();
  }

  /** Rebuilds the optgroup structure from filteredOptions — options without a `group` render flat, preserving order. */
  private buildGroups() {
    this.hasGroups = this.options.some(o => !!o.group);
    if (!this.hasGroups) {
      this.groupedFilteredOptions = [];
      return;
    }
    const groups: { name: string; options: SelectOption[] }[] = [];
    for (const opt of this.filteredOptions) {
      const name = opt.group || '';
      let g = groups.length && groups[groups.length - 1].name === name
        ? groups[groups.length - 1]
        : groups.find(x => x.name === name);
      if (!g) {
        g = { name, options: [] };
        groups.push(g);
      }
      g.options.push(opt);
    }
    this.groupedFilteredOptions = groups;
    if (this.groupsCollapsedByDefault && this.collapseNewGroupsIndividually) {
      groups.forEach(g => {
        if (g.name && !this.initializedGroups.has(g.name)) {
          this.collapsedGroups.add(g.name);
          this.initializedGroups.add(g.name);
        }
      });
    } else if (this.groupsCollapsedByDefault && !this.collapseInitialized && groups.length) {
      groups.forEach(g => { if (g.name) this.collapsedGroups.add(g.name); });
      this.collapseInitialized = true;
    }
  }
  private collapseInitialized = false;
  private initializedGroups = new Set<string>();

  get hasSelection(): boolean {
    if (this.multiple) return Array.isArray(this.value) && this.value.length > 0;
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get selectedLabel(): string {
    const opt = this.options.find(o => o.value === this.value);
    return opt ? opt.label : '';
  }

  get selectedItems(): SelectOption[] {
    if (!this.multiple || !Array.isArray(this.value)) return [];
    return this.value
      .map(v => this.options.find(o => o.value === v))
      .filter((o): o is SelectOption => !!o);
  }

  /** Comma-joined labels of the selected items (multi-select trigger text). */
  get selectedItemsLabels(): string {
    return this.selectedItems.map(o => o.label).join(', ');
  }

  get viewportHeight(): number {
    return Math.min(this.filteredOptions.length, 7) * this.itemSize;
  }

  isSelected(opt: SelectOption): boolean {
    if (this.multiple) return Array.isArray(this.value) && this.value.includes(opt.value);
    return this.value === opt.value;
  }

  toggleDropdown() {
    if (this.disabled) return;
    if (!this.isOpen) {
      this.triggerWidth = this.triggerRef?.nativeElement?.offsetWidth ?? 240;
      this.isOpen = true;
      this.panelOpenedChange.emit(true);
      if (!this.customPanel) {
        setTimeout(() => this.vsSearchInput?.nativeElement?.focus(), 50);
      }
    } else {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    this.isOpen = false;
    if (!this.customPanel) {
      this.searchQuery = '';
      this.applyFilter();
    }
    this.onTouched();
    this.panelOpenedChange.emit(false);
  }

  selectOption(opt: SelectOption) {
    if (opt.disabled) return;
    if (this.multiple) {
      const arr: any[] = Array.isArray(this.value) ? [...this.value] : [];
      const idx = arr.indexOf(opt.value);
      if (idx > -1) arr.splice(idx, 1);
      else arr.push(opt.value);
      this.value = arr;
    } else {
      this.value = opt.value;
      this.closeDropdown();
    }
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  removeItem(item: SelectOption, event: Event) {
    event.stopPropagation();
    if (!this.multiple || !Array.isArray(this.value)) return;
    this.value = this.value.filter(v => v !== item.value);
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    this.value = this.multiple ? [] : null;
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  // ── Select-all (multi-select) ────────────────────────────────────────────────

  private selectedArray(): any[] {
    return Array.isArray(this.value) ? this.value : [];
  }

  private enabledValues(opts: SelectOption[]): any[] {
    return opts.filter(o => !o.disabled).map(o => o.value);
  }

  areAllSelected(opts: SelectOption[]): boolean {
    const vals = this.enabledValues(opts);
    const sel = this.selectedArray();
    return vals.length > 0 && vals.every(v => sel.includes(v));
  }

  areSomeSelected(opts: SelectOption[]): boolean {
    const vals = this.enabledValues(opts);
    const sel = this.selectedArray();
    const count = vals.filter(v => sel.includes(v)).length;
    return count > 0 && count < vals.length;
  }

  /** Toggles every enabled option in `opts` (the full filtered list or one group's options). */
  toggleAll(opts: SelectOption[], checked: boolean) {
    const vals = this.enabledValues(opts);
    let arr = this.selectedArray().slice();
    if (checked) {
      vals.forEach(v => { if (!arr.includes(v)) arr.push(v); });
    } else {
      arr = arr.filter(v => !vals.includes(v));
    }
    this.value = arr;
    this.onChange(arr);
    this.onTouched();
    if (!this.confirmFooter) this.selectionChange.emit(arr);
  }

  /** Collapsed group names (grouped + select-all mode). Searching overrides collapse so matches stay visible. */
  private collapsedGroups = new Set<string>();

  toggleGroupCollapse(name: string, ev: Event) {
    ev.stopPropagation();
    if (this.collapsedGroups.has(name)) this.collapsedGroups.delete(name);
    else this.collapsedGroups.add(name);
  }

  isGroupCollapsed(name: string): boolean {
    return !this.searchQuery && this.collapsedGroups.has(name);
  }

  onSearch(query: string) {
    this.searchQuery = query;
    this.applyFilter();
    this.searchChange.emit(query);
  }

  onMatValueChange(val: any) {
    this.value = val;
    this.onChange(val);
    this.onTouched();
    // Undo mat-select's post-selection scroll-into-view (see scrollGuardHandler) so the list
    // stays put when the user toggles an option with the mouse. The scroll already happened
    // synchronously before this handler runs, so restoring here paints only the final position.
    if (this.multiple && this.savedScrollTop != null && this.guardedPanel) {
      this.guardedPanel.scrollTop = this.savedScrollTop;
    }
    // confirmFooter defers only the "apply" notification (selectionChange/confirm) until OK —
    // the bound value still updates live, so the trigger stays in sync natively.
    if (!this.confirmFooter) this.selectionChange.emit(val);
  }

  /** Standard mat-select open/close. In confirmFooter mode, snapshot the value on open so Cancel can restore it. */
  onMatPanelToggle(open: boolean) {
    if (open) {
      if (this.confirmFooter) {
        this.confirmSnapshot = Array.isArray(this.value) ? [...this.value] : this.value;
      }
      this.attachScrollGuard();
    } else {
      this.detachScrollGuard();
    }
    this.panelOpenedChange.emit(open);
  }

  private attachScrollGuard() {
    const panel = this.matSelectRef?.panel?.nativeElement as HTMLElement | undefined;
    if (!panel) return;
    this.guardedPanel = panel;
    this.savedScrollTop = null;
    panel.addEventListener('mousedown', this.scrollGuardHandler);
  }

  private detachScrollGuard() {
    if (this.guardedPanel) {
      this.guardedPanel.removeEventListener('mousedown', this.scrollGuardHandler);
    }
    this.guardedPanel = null;
    this.savedScrollTop = null;
  }

  ngOnDestroy() {
    this.detachScrollGuard();
  }

  /** confirmFooter mode: notify that the (already-live) selection is applied, then close. */
  confirmSelection() {
    this.selectionChange.emit(this.value);
    this.confirm.emit(this.value);
    this.matSelectRef?.close();
  }

  /** confirmFooter mode: restore the selection captured when the panel opened, then close. */
  cancelSelection() {
    this.value = Array.isArray(this.confirmSnapshot) ? [...this.confirmSnapshot] : this.confirmSnapshot;
    this.onChange(this.value);
    this.cancel.emit();
    this.matSelectRef?.close();
  }

  /** `creatable`: show the create row when there's a query that matches no existing option label. */
  get showCreateRow(): boolean {
    const q = this.searchQuery.trim();
    if (!this.creatable || !q) return false;
    return !this.options.some(o => (o.label || '').toLowerCase() === q.toLowerCase());
  }

  onCreate() {
    this.createOption.emit(this.searchQuery.trim());
  }

  onScrollIndexChange(index: number) {
    if (index >= this.filteredOptions.length - 8) this.scrolledToEnd.emit();
  }

  writeValue(value: any) {
    this.value = value ?? (this.multiple ? [] : null);
  }
  registerOnChange(fn: any) { this.onChange = fn; }
  registerOnTouched(fn: any) { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean) { this.disabled = isDisabled; }
}
